import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue";
const _withScopeId = n => (_pushScopeId("data-v-e50e36c8"), n = n(), _popScopeId(), n);
const _hoisted_1 = { class: "sellers-table" };
const _hoisted_2 = { class: "table__header" };
const _hoisted_3 = { class: "table__cell--highlight table__cell--ellipsis" };
const _hoisted_4 = { class: "table__header" };
const _hoisted_5 = { class: "table__cell" };
const _hoisted_6 = { class: "table__header" };
const _hoisted_7 = { class: "table__cell" };
const _hoisted_8 = { class: "table__header" };
const _hoisted_9 = { class: "table__cell" };
const _hoisted_10 = { class: "table__header" };
const _hoisted_11 = { class: "table__cell table__cell--ellipsis" };
const _hoisted_12 = { class: "table__header" };
const _hoisted_13 = { class: "table__cell" };
const _hoisted_14 = { class: "table__header" };
const _hoisted_15 = { class: "table__cell-status" };
const _hoisted_16 = { class: "table__cell" };
const _hoisted_17 = { class: "table__empty" };
import i18n from '@/i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
// data
import { fetchSellersList } from '../data/fetchSellers';
// components
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner.vue';
import InfoCard from '@/components/_atoms/InfoCard.vue';
import ListPagination from '@/components/_atoms/ListPagination.vue';
import QuestionModal from '@/components/oto/sellers/QuestionModal.vue';
import DeletePersona from '@/components/personas/_organisms/DeletePersona.vue';
import TableColumnSearch from '@/components/TableColumnSearch.vue';
import bus from '@/helpers/events/bus';
import router from '@/router';
const STALE_TIME = 1000 * 60 * 60;
export default /*#__PURE__*/ _defineComponent({
    __name: 'SellersTable',
    setup(__props) {
        // libs
        const tableRef = ref();
        const store = useStore();
        const queryClient = useQueryClient();
        const id = ref('sellers-list');
        const pagination = reactive({ count: 0, limit: 20, page: 1 });
        const previousPage = ref(1);
        const getFilterValues = computed(() => store.getters['sellers/getFilterValues']);
        const getStatusFilter = computed(() => store.getters['sellers/getStatusFilter']);
        const getOrderBy = computed(() => store.getters['sellers/getOrderBy']);
        const getFormattedFilters = computed(() => store.getters['sellers/getFormattedFilters']);
        const getEnabledValue = computed(() => store.getters['sellers/getEnabledValue']);
        const selectedBrand = computed(() => store.getters.selectedBrand);
        const filteredValues = computed(() => {
            const statusValue = getStatusFilter.value;
            if (Array.isArray(statusValue)) {
                return statusValue;
            }
            if (statusValue && statusValue !== 'active') {
                return [statusValue];
            }
            return ['active'];
        });
        const currentSort = computed(() => {
            if (!getOrderBy.value)
                return;
            const isDescending = getOrderBy.value.startsWith('-');
            const prop = isDescending ? getOrderBy.value.substring(1) : getOrderBy.value;
            const order = isDescending ? 'descending' : 'ascending';
            return { prop, order };
        });
        const sellersQueryKey = computed(() => createSellersQueryKey(pagination.page));
        const sellersPrefetchQueryKey = computed(() => createSellersQueryKey(pagination.page + 1));
        const { data: sellersData, isPending: loading, error: hasError, } = useQuery({
            queryKey: sellersQueryKey,
            queryFn: () => createFetchSellersQuery(pagination.page),
            staleTime: STALE_TIME,
        });
        queryClient.prefetchQuery({
            queryKey: sellersPrefetchQueryKey,
            queryFn: () => createFetchSellersQuery(pagination.page + 1),
            staleTime: STALE_TIME,
        });
        const sellersList = computed(() => sellersData.value?.sellers || []);
        const sellersCount = computed(() => sellersData.value?.totalCount || 0);
        function createFetchSellersQuery(page) {
            const fetchParams = {
                page,
                limit: pagination.limit,
                orderBy: getOrderBy.value,
                filters: getFormattedFilters.value,
                statusFilter: getEnabledValue.value(getStatusFilter.value),
            };
            return fetchSellersList(fetchParams.filters, fetchParams.orderBy, fetchParams.page, fetchParams.limit, fetchParams.statusFilter);
        }
        function createSellersQueryKey(page) {
            return [
                'sellers',
                {
                    page,
                    limit: pagination.limit,
                    orderBy: getOrderBy.value,
                    filters: getFormattedFilters.value,
                    statusFilter: getStatusFilter.value,
                    brandId: selectedBrand.value?.id,
                },
            ];
        }
        function formatStatusLabel(status) {
            return status === 'active' ? i18n.global.t('_active') : i18n.global.t('_inactive');
        }
        function sortTable({ prop, order }) {
            const orderBy = !order || order === 'ascending' ? prop : `-${prop}`;
            store.dispatch('sellers/setOrderBy', orderBy);
        }
        function filterChangeHandler(activeFilters) {
            const statusValues = activeFilters.status || [];
            const statusFilter = statusValues.length > 1 ? statusValues : statusValues[0] || 'active';
            resetPagination();
            store.dispatch('sellers/setStatusFilter', statusFilter);
        }
        /**
         * Sets the currently selected row in the table.
         * @param row - The seller row to select. If ``undefined``, clears the current selection.
         */
        function clearRowSelection(row) {
            tableRef.value?.setCurrentRow(row);
        }
        function navigateToSellerDetail(row) {
            if (!row || !row.id)
                return;
            router.push({
                name: 'store-management-sellers-detail',
                params: { sellerId: row.id.toString() },
            });
            clearRowSelection();
        }
        function resetPagination() {
            const FIRST_PAGE = 1;
            pagination.page = FIRST_PAGE;
            previousPage.value = FIRST_PAGE;
        }
        function handleSearchTerm(nameSearch, { property }) {
            const filter = { [property]: nameSearch };
            resetPagination();
            store.dispatch('sellers/setFilterValues', filter);
        }
        function clearTerm({ property }) {
            const filter = { [property]: '' };
            resetPagination();
            store.dispatch('sellers/setFilterValues', filter);
        }
        function reloadSellersList() {
            queryClient.invalidateQueries({ queryKey: ['sellers'] });
        }
        watch(sellersCount, newCount => {
            pagination.count = newCount;
        }, { immediate: true });
        watch(() => sellersPrefetchQueryKey.value, newKey => {
            const totalPages = Math.ceil(sellersCount.value / pagination.limit);
            const hasNextPage = pagination.page < totalPages;
            const shouldPrefetch = pagination.page >= previousPage.value && hasNextPage;
            if (shouldPrefetch) {
                queryClient.prefetchQuery({
                    queryKey: newKey,
                    queryFn: () => createFetchSellersQuery(pagination.page + 1),
                    staleTime: STALE_TIME,
                });
            }
            previousPage.value = pagination.page;
        });
        onMounted(() => {
            bus.$on('sellers-reload-list', reloadSellersList);
        });
        onUnmounted(() => {
            store.dispatch('sellers/resetFilterValues');
            store.dispatch('sellers/setOrderBy', 'name');
            bus.$off('sellers-reload-list', reloadSellersList);
        });
        return (_ctx, _cache) => {
            const _component_el_table_column = _resolveComponent("el-table-column");
            const _component_el_table = _resolveComponent("el-table");
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                (!_unref(loading) && !_unref(hasError))
                    ? (_openBlock(), _createBlock(_component_el_table, {
                        key: 0,
                        data: sellersList.value,
                        ref_key: "tableRef",
                        ref: tableRef,
                        stripe: "",
                        class: "table",
                        "max-height": "2500",
                        "highlight-current-row": "",
                        "default-sort": currentSort.value,
                        onSortChange: sortTable,
                        onCurrentChange: navigateToSellerDetail,
                        onFilterChange: _cache[0] || (_cache[0] = activeFilters => filterChangeHandler(activeFilters))
                    }, {
                        empty: _withCtx(() => [
                            _createElementVNode("div", _hoisted_17, [
                                _createVNode(InfoCard, {
                                    "no-search-results": !sellersList.value.length
                                }, null, 8, ["no-search-results"])
                            ])
                        ]),
                        default: _withCtx(() => [
                            _createVNode(_component_el_table_column, {
                                prop: "name",
                                label: _ctx.$t('store-management.sellers.columns.name'),
                                sortable: "custom",
                                width: "240",
                                "show-overflow-tooltip": ""
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_2, _toDisplayString(column.label), 1),
                                    _createVNode(TableColumnSearch, {
                                        searchTerm: getFilterValues.value.name,
                                        "onUpdate:nameSearch": searchString => handleSearchTerm(searchString, column),
                                        "onClear:nameSearch": () => clearTerm(column)
                                    }, {
                                        reference: _withCtx(() => [
                                            _createElementVNode("i", {
                                                class: _normalizeClass(["icon icon-search-12px", { 'search-icon--active': getFilterValues.value.name }])
                                            }, null, 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["searchTerm", "onUpdate:nameSearch", "onClear:nameSearch"])
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_3, _toDisplayString(row.name), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "clients",
                                label: _ctx.$t('store-management.sellers.columns.customers'),
                                sortable: "custom",
                                width: "100"
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_4, _toDisplayString(column.label), 1)
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_5, _toDisplayString(row.customers), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "enrolment",
                                label: _ctx.$t('store-management.sellers.columns.enrollment'),
                                sortable: "custom",
                                width: "140"
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_6, _toDisplayString(column.label), 1),
                                    _createVNode(TableColumnSearch, {
                                        searchTerm: getFilterValues.value.enrolment,
                                        "onUpdate:nameSearch": searchString => handleSearchTerm(searchString, column),
                                        "onClear:nameSearch": () => clearTerm(column)
                                    }, {
                                        reference: _withCtx(() => [
                                            _createElementVNode("i", {
                                                class: _normalizeClass(["icon icon-search-12px", { 'search-icon--active': getFilterValues.value.enrolment }])
                                            }, null, 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["searchTerm", "onUpdate:nameSearch", "onClear:nameSearch"])
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_7, _toDisplayString(row.enrolment), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "whatsapp",
                                label: _ctx.$t('store-management.sellers.columns.whatsapp'),
                                sortable: "custom",
                                width: "140"
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_8, _toDisplayString(column.label), 1),
                                    _createVNode(TableColumnSearch, {
                                        searchTerm: getFilterValues.value.whatsapp,
                                        "onUpdate:nameSearch": searchString => handleSearchTerm(searchString, column),
                                        "onClear:nameSearch": () => clearTerm(column)
                                    }, {
                                        reference: _withCtx(() => [
                                            _createElementVNode("i", {
                                                class: _normalizeClass(["icon icon-search-12px", { 'search-icon--active': getFilterValues.value.whatsapp }])
                                            }, null, 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["searchTerm", "onUpdate:nameSearch", "onClear:nameSearch"])
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_9, _toDisplayString(row.whatsApp || '-'), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "storeName",
                                label: _ctx.$t('store-management.sellers.columns.storeName'),
                                sortable: "custom",
                                width: "200",
                                "show-overflow-tooltip": ""
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_10, _toDisplayString(column.label), 1),
                                    _createVNode(TableColumnSearch, {
                                        searchTerm: getFilterValues.value.storeName,
                                        "onUpdate:nameSearch": searchString => handleSearchTerm(searchString, column),
                                        "onClear:nameSearch": () => clearTerm(column)
                                    }, {
                                        reference: _withCtx(() => [
                                            _createElementVNode("i", {
                                                class: _normalizeClass(["icon icon-search-12px", { 'search-icon--active': getFilterValues.value.storeName }])
                                            }, null, 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["searchTerm", "onUpdate:nameSearch", "onClear:nameSearch"])
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_11, _toDisplayString(row.storeName || '-'), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "store",
                                label: _ctx.$t('store-management.sellers.columns.store'),
                                sortable: "custom",
                                width: "160"
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_12, _toDisplayString(column.label), 1),
                                    _createVNode(TableColumnSearch, {
                                        searchTerm: getFilterValues.value.store,
                                        "onUpdate:nameSearch": searchString => handleSearchTerm(searchString, column),
                                        "onClear:nameSearch": () => clearTerm(column)
                                    }, {
                                        reference: _withCtx(() => [
                                            _createElementVNode("i", {
                                                class: _normalizeClass(["icon icon-search-12px", { 'search-icon--active': getFilterValues.value.store }])
                                            }, null, 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["searchTerm", "onUpdate:nameSearch", "onClear:nameSearch"])
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("span", _hoisted_13, _toDisplayString(row.store), 1)
                                ]),
                                _: 1
                            }, 8, ["label"]),
                            _createVNode(_component_el_table_column, {
                                prop: "status",
                                label: "Status",
                                "column-key": "status",
                                filters: [
                                    { text: _unref(i18n).global.t('_active'), value: 'active' },
                                    { text: _unref(i18n).global.t('_inactive'), value: 'inactive' },
                                ],
                                "filtered-value": filteredValues.value,
                                width: "100"
                            }, {
                                header: _withCtx(({ column }) => [
                                    _createElementVNode("span", _hoisted_14, _toDisplayString(column.label), 1)
                                ]),
                                default: _withCtx(({ row }) => [
                                    _createElementVNode("div", _hoisted_15, [
                                        _createElementVNode("span", {
                                            class: _normalizeClass(`dot dot__${row.status}`)
                                        }, null, 2),
                                        _createElementVNode("span", _hoisted_16, _toDisplayString(formatStatusLabel(row.status)), 1)
                                    ])
                                ]),
                                _: 1
                            }, 8, ["filters", "filtered-value"])
                        ]),
                        _: 1
                    }, 8, ["data", "default-sort"]))
                    : _createCommentVNode("", true),
                (_unref(loading))
                    ? (_openBlock(), _createBlock(BaseLoader, { key: 1 }))
                    : _createCommentVNode("", true),
                (_unref(hasError))
                    ? (_openBlock(), _createBlock(InfoCard, {
                        key: 2,
                        ref: "message-error",
                        error: _unref(hasError)
                    }, null, 8, ["error"]))
                    : _createCommentVNode("", true),
                (!_unref(loading) && pagination.count > 0)
                    ? (_openBlock(), _createBlock(ListPagination, {
                        key: 3,
                        data: pagination,
                        limit: pagination.limit,
                        "limit-range": [10, 20, 50],
                        identify: id.value,
                        "show-list-pages": false,
                        "show-quantity-lines": true
                    }, null, 8, ["data", "limit", "identify"]))
                    : _createCommentVNode("", true),
                _createVNode(DeletePersona),
                _createVNode(QuestionModal)
            ]));
        };
    }
});
